import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import useScrollSync, {ScrollSyncDirection} from 'ui-library/hooks/useScrollSync';

export interface PersistentHorizontalScrollbarProps {
  horizontalScrollContainer: HTMLElement | null;
  table: HTMLElement | null;
  isCollapsed: boolean;
}

/**
 * Renders a viewport-sticky horizontal scrollbar that stays visible at the bottom of the screen
 * when the table's native scrollbar has scrolled out of view. This lets users scroll wide tables
 * horizontally without first scrolling to the bottom.
 *
 * The proxy div is always rendered (so useScrollSync can keep scrollLeft in sync even while
 * hidden), but visually collapsed to zero height when the native scrollbar is in view.
 */
export default function PersistentHorizontalScrollbar({
  horizontalScrollContainer,
  table,
  isCollapsed,
}: PersistentHorizontalScrollbarProps): JSX.Element {
  const proxyRef = useRef<HTMLDivElement>(null);
  const rafId = useRef<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [contentWidth, setContentWidth] = useState(0);

  // Bidirectionally sync scrollLeft between the real scroll container and this proxy div.
  useScrollSync(horizontalScrollContainer, proxyRef.current, ScrollSyncDirection.HORIZONTAL);

  // Recompute whether the proxy should be visible. The native scrollbar is hidden when the
  // scroll area's bottom extends past the top of the sticky footer. We use the proxy div's
  // own bottom edge as the threshold since it always aligns with the footer's top.
  const updateVisibility = useCallback(() => {
    if (!horizontalScrollContainer || isCollapsed) {
      setIsVisible(false);
      return;
    }

    const scrollWidth = horizontalScrollContainer.scrollWidth;
    const hasOverflow = scrollWidth > horizontalScrollContainer.clientWidth;
    if (!hasOverflow) {
      setIsVisible(false);
      return;
    }

    setContentWidth(scrollWidth);

    const scrollAreaRect = horizontalScrollContainer.getBoundingClientRect();
    const stickyFooterTop = proxyRef.current?.getBoundingClientRect().bottom ?? window.innerHeight;

    // floor these measurements to avoid floating point precision issues
    const nativeScrollbarIsHidden = Math.floor(scrollAreaRect.bottom) > Math.floor(stickyFooterTop);
    const tableIsBelowViewport = scrollAreaRect.top > window.innerHeight;
    const tableIsAboveViewport = scrollAreaRect.bottom < 0;

    setIsVisible(nativeScrollbarIsHidden && !tableIsAboveViewport && !tableIsBelowViewport);
  }, [horizontalScrollContainer, isCollapsed]);

  // RAF-throttled wrapper around updateVisibility. All callers — scroll events,
  // window resize, and ResizeObserver — go through this so that visibility is
  // never recomputed synchronously inside a ResizeObserver callback.
  //
  // Without this, the ResizeObserver creates a layout feedback loop:
  //   observer fires → updateVisibility() → setIsVisible(true) → proxy div
  //   gains height → stickyBottomContainer layout shifts → observer fires
  //   again → … (crashes Chromium renderer)
  //
  // Deferring to the next animation frame lets the browser paint between
  // iterations and the RAF dedup naturally collapses redundant updates.
  // NOTE: I was never able to reproduce this issue myself, and only ever saw it occur in
  // AutoLinkItemsToCustomStudy-test.ts. It's possible that it's restricted just to the
  // browser version that Cypress uses, or some other environmental factor.
  const scheduleVisibilityUpdate = useCallback((): void => {
    if (rafId.current == null) {
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        updateVisibility();
      });
    }
  }, [updateVisibility]);

  // Listen to scroll/resize events to keep visibility up to date. The window capture listener
  // covers both horizontal and vertical scroll sources.
  useEffect(() => {
    scheduleVisibilityUpdate();

    window.addEventListener('scroll', scheduleVisibilityUpdate, true);
    window.addEventListener('resize', scheduleVisibilityUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleVisibilityUpdate, true);
      window.removeEventListener('resize', scheduleVisibilityUpdate);
      if (rafId.current != null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [scheduleVisibilityUpdate]);

  // Track size changes on the scroll container and inner table so the proxy's
  // width stays in sync and visibility is re-evaluated. Observing the <table>
  // element is critical because column resizes change its width without
  // necessarily changing the wrapper's bounding dimensions.
  useEffect(() => {
    if (!horizontalScrollContainer || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (horizontalScrollContainer) {
        setContentWidth(horizontalScrollContainer.scrollWidth);
        scheduleVisibilityUpdate();
      }
    });

    observer.observe(horizontalScrollContainer);
    if (table) {
      observer.observe(table);
    }

    return () => {
      observer.disconnect();
    };
  }, [horizontalScrollContainer, table, scheduleVisibilityUpdate]);

  const {classes} = useStyles();

  // Use height: auto to account for differently sized native scrollbars in different browsers.
  return (
    <div
      ref={proxyRef}
      className={`mediocre-tableEditable-persistentScrollbar ${classes.proxy}`}
      {...getTestProps({component: 'PersistentHorizontalScrollbar'})}
      style={{
        height: isVisible ? 'auto' : 0,
        overflow: isVisible ? undefined : 'hidden',
      }}
    >
      <div
        className={classes.inner}
        {...getTestProps({component: 'PersistentHorizontalScrollbarInner'})}
        style={{width: contentWidth}}
      />
    </div>
  );
}

const useStyles = createStyles(() => ({
  proxy: {
    width: '100%',
  },
  inner: {
    height: 1,
  },
}));
